import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Truck, MapPin, Euro, CheckCircle, ArrowRight, Clock, Star, Wallet } from 'lucide-react';
import { PageHeader, PageFooter } from './CustomersPage';

export default function DriversPage() {
  const navigate = useNavigate();

  const steps = [
    { num: '1', title: 'Browse Jobs', desc: 'See available delivery jobs in your area. Filter by county, item type, and more.', icon: MapPin },
    { num: '2', title: 'Submit Your Offer', desc: 'Set your own price for each job. The customer decides — no middleman rates.', icon: Euro },
    { num: '3', title: 'Deliver & Get Paid', desc: 'Complete the delivery, enter the verification code, and the customer pays you directly.', icon: CheckCircle },
  ];

  const benefits = [
    { icon: Clock, title: 'Be Your Own Boss', desc: 'Work when you want. No shifts, no minimum hours, no commitments.' },
    { icon: Truck, title: 'Choose Your Jobs', desc: 'Pick the deliveries that suit your route, schedule, and vehicle.' },
    { icon: Wallet, title: 'Get Paid Per Job', desc: 'Set your own price. Customers pay you directly — no payout account needed.' },
    { icon: Star, title: 'Build Your Reputation', desc: 'Earn reviews from customers. A strong profile wins more work.' },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="drivers-page">
      <PageHeader />

      {/* Hero */}
      <section className="py-16 sm:py-24 bg-gradient-to-b from-orange-50/50 to-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#F57C00]">For Drivers</span>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-gray-900">
            Earn Money<br /><span className="text-[#F57C00]">On Your Schedule</span>
          </h1>
          <p className="mt-5 text-lg text-gray-600 max-w-2xl mx-auto">
            Browse delivery jobs near you, set your own price, and get paid after every job. No boss, no fixed hours — just you and the road.
          </p>
          <button onClick={() => navigate('/register')} className="mt-8 inline-flex items-center gap-2 px-8 py-4 bg-[#F57C00] text-white font-bold text-base hover:bg-[#E65100] transition-all active:scale-95 rounded-md" data-testid="drivers-cta">
            <Truck className="w-5 h-5" /> Start Driving <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 sm:py-20">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-center text-2xl sm:text-3xl font-bold text-gray-900">How It Works</h2>
          <div className="mt-12 grid md:grid-cols-3 gap-8">
            {steps.map(({ num, title, desc, icon: Icon }) => (
              <div key={num} className="text-center">
                <div className="w-14 h-14 rounded-full bg-[#F57C00] text-white flex items-center justify-center text-xl font-bold mx-auto">{num}</div>
                <Icon className="w-6 h-6 text-[#F57C00] mx-auto mt-4" strokeWidth={1.5} />
                <h3 className="mt-3 text-lg font-bold text-gray-900">{title}</h3>
                <p className="mt-2 text-sm text-gray-600 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 sm:py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-center text-2xl sm:text-3xl font-bold text-gray-900">Why Drivers Love JustDeliver</h2>
          <div className="mt-12 grid sm:grid-cols-2 gap-6">
            {benefits.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="bg-white border border-gray-200 rounded-xl p-6 hover:-translate-y-0.5 transition-transform">
                <Icon className="w-6 h-6 text-[#F57C00]" strokeWidth={1.5} />
                <h3 className="mt-3 text-base font-bold text-gray-900">{title}</h3>
                <p className="mt-1.5 text-sm text-gray-600">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 sm:py-20 text-center">
        <div className="max-w-2xl mx-auto px-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Ready to earn?</h2>
          <p className="mt-3 text-gray-600">Sign up in under a minute. Start browsing jobs today.</p>
          <button onClick={() => navigate('/register')} className="mt-8 inline-flex items-center gap-2 px-8 py-4 bg-[#F57C00] text-white font-bold hover:bg-[#E65100] transition-all active:scale-95 rounded-md">
            <Truck className="w-5 h-5" /> Start Driving
          </button>
        </div>
      </section>

      <PageFooter />
    </div>
  );
}
